/**
 * Display rules for equipment insurance. The windows mirror the backend's
 * reminder (HHNodeServer equipmentReminderScheduler: INSURANCE_WARN_DAYS 30,
 * INSURANCE_URGENT_DAYS 7), so a policy the page calls "due" is one somebody
 * has been told about.
 */

import type { InsuranceCoverType, InsurancePolicy } from '../types/equipmentInsurance';
import { num } from './nurseryMoney';

export const INSURANCE_WARN_DAYS = 30;
export const INSURANCE_URGENT_DAYS = 7;

export const COVER_TYPE_LABELS: Record<InsuranceCoverType, string> = {
  comprehensive: 'Comprehensive',
  third_party_property: 'Third party property',
  third_party_fire_theft: 'Third party, fire and theft',
  farm_pack: 'Farm pack',
  machinery: 'Machinery breakdown',
  other: 'Other',
};

export type InsuranceTone = 'ended' | 'expired' | 'urgent' | 'due' | 'ok';

export interface InsuranceState {
  tone: InsuranceTone;
  label: string;
}

/**
 * Where a policy stands, from its status and days left. `days_left` comes from
 * the server (expiry − CURRENT_DATE), so the page and the reminder agree on
 * which day it lapses.
 */
export function insuranceState(p: Pick<InsurancePolicy, 'status' | 'days_left'>): InsuranceState {
  if (p.status === 'renewed') return { tone: 'ended', label: 'Renewed' };
  if (p.status === 'cancelled') return { tone: 'ended', label: 'Cancelled' };
  const d = p.days_left;
  if (d < 0) return { tone: 'expired', label: d === -1 ? 'Expired yesterday' : `Expired ${-d} days ago` };
  if (d === 0) return { tone: 'urgent', label: 'Expires today' };
  if (d === 1) return { tone: 'urgent', label: 'Expires tomorrow' };
  if (d <= INSURANCE_URGENT_DAYS) return { tone: 'urgent', label: `Expires in ${d} days` };
  if (d <= INSURANCE_WARN_DAYS) return { tone: 'due', label: `Expires in ${d} days` };
  return { tone: 'ok', label: 'Current' };
}

export const TONE_CLASSES: Record<InsuranceTone, string> = {
  ended: 'bg-neutral-100 text-neutral-600',
  expired: 'bg-red-100 text-red-700',
  urgent: 'bg-red-50 text-red-700',
  due: 'bg-amber-100 text-amber-800',
  ok: 'bg-green-100 text-green-700',
};

const csvCell = (v: string | number | null | undefined): string => {
  const s = v == null ? '' : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/**
 * The asset schedule a broker asks for at renewal: one row per machine on a
 * current policy. Agreed values only when the reader was sent them.
 */
export function insuranceScheduleCsv(policies: InsurancePolicy[]): string {
  const header = [
    'Insurer', 'Policy number', 'Cover', 'Start', 'Expiry',
    'Machine', 'Make', 'Model', 'Year', 'Rego', 'VIN', 'Serial number', 'Agreed value',
  ];
  const rows: (string | number | null | undefined)[][] = [];
  for (const p of policies) {
    if (p.status !== 'current') continue;
    const items = p.items ?? [];
    const base = [p.insurer, p.policy_number, COVER_TYPE_LABELS[p.cover_type] ?? p.cover_type, p.start_date, p.expiry_date];
    if (!items.length) {
      rows.push([...base, '', '', '', '', '', '', '', '']);
      continue;
    }
    for (const it of items) {
      rows.push([
        ...base, it.equipment_name, it.make, it.model, it.year_of_manufacture, it.rego_number, it.vin,
        it.serial_number, it.agreed_value == null ? '' : num(it.agreed_value).toFixed(2),
      ]);
    }
  }
  return [header, ...rows].map(r => r.map(csvCell).join(',')).join('\n');
}
